import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import Icon from '../../components/Icon'
import ConfirmModal from '../../components/ConfirmModal'
import { Button, FormGroup, Input, Select } from '../../components/ui'
import { formatRub } from '../../utils/formatRub'
import './Company.css'

const EMPTY_WORK_ITEM = { name: '', due_date: '' }

function todayIsoDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function contractLabel(contract) {
  return [contract?.number || contract?.id, contract?.object_name].filter(Boolean).join(' · ')
}

function formatBudgetDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU')
}

function normalizeWorkItems(items) {
  const rows = (items || []).length ? items : [EMPTY_WORK_ITEM]
  return rows.map((item) => ({
    id: item.id,
    name: item.name || '',
    due_date: item.due_date || item.end_date || item.deadline || '',
  }))
}

function ObjectStageForm() {
  const { stageId, id } = useParams()
  const editStageId = stageId || id
  const isEdit = Boolean(editStageId)
  const navigate = useNavigate()
  const location = useLocation()

  const [objects, setObjects] = useState([])
  const [contracts, setContracts] = useState([])
  const [stageTypes, setStageTypes] = useState([])
  const [stageBudgets, setStageBudgets] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // Ошибки валидации: подсветка пустых/некорректных полей после нажатия «Сохранить».
  const [fieldErrors, setFieldErrors] = useState({ work_start_date: false, work_items: [] })
  const [contractModalOpen, setContractModalOpen] = useState(false)
  const [contractSearch, setContractSearch] = useState('')
  const [selectedContractId, setSelectedContractId] = useState('')
  const [form, setForm] = useState({
    object_id: location.state?.objectId || '',
    type: '',
    name: '',
    status: 'в работе',
    planned_date: '',
    fact_date: '',
    completion_date: '',
    work_start_date: todayIsoDate(),
    work_items: [EMPTY_WORK_ITEM],
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const [objectsResponse, contractsResponse, stageTypesResponse, stageResponse] = await Promise.all([
          api.get('/company/objects?limit=200'),
          api.get('/company/contracts?limit=200'),
          api.get('/company/stage-types'),
          isEdit ? api.get(`/company/object-stages/${encodeURIComponent(editStageId)}`) : Promise.resolve(null),
        ])
        if (cancelled) return
        setObjects(objectsResponse.data?.objects || [])
        setContracts(contractsResponse.data?.contracts || [])
        setStageTypes(stageTypesResponse.data?.stage_types || [])
        if (stageResponse?.data?.stage) {
          const stage = stageResponse.data.stage
          setStageBudgets(stage.budgets || [])
          setForm({
            object_id: stage.object_id || '',
            type: stage.type || '',
            name: stage.name || stage.type || '',
            status: stage.status || 'не в работе',
            planned_date: stage.planned_date || '',
            fact_date: stage.fact_date || '',
            completion_date: stage.completion_date || '',
            work_start_date: stage.work_start_date || '',
            work_items: normalizeWorkItems(stage.work_items),
          })
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.response?.data?.error || 'Не удалось загрузить форму стадии')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [editStageId, isEdit])

  const filteredContracts = useMemo(() => (
    contracts.filter((contract) => !form.object_id || String(contract.object_id) === String(form.object_id))
  ), [contracts, form.object_id])

  const filteredContractsForCreate = useMemo(() => (
    filteredContracts.filter((contract) => (
      String(contract.number || contract.id || '')
        .toLowerCase()
        .includes(contractSearch.trim().toLowerCase())
    ))
  ), [contractSearch, filteredContracts])

  const budgetsTotal = useMemo(() => (
    stageBudgets.reduce((sum, budget) => sum + Number(budget.amount_with_vat || 0), 0)
  ), [stageBudgets])

  const handleField = (field, value) => {
    if (field === 'work_start_date') {
      setFieldErrors((prev) => ({ ...prev, work_start_date: false }))
    }
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'type' && !prev.name) {
        next.name = value
      }
      if (field === 'status' && value === 'в работе' && !prev.work_start_date) {
        next.work_start_date = todayIsoDate()
      }
      if (field === 'object_id') {
        setSelectedContractId('')
      }
      return next
    })
  }

  const handleWorkItem = (index, field, value) => {
    setFieldErrors((prev) => {
      const rows = [...(prev.work_items || [])]
      if (rows[index]) rows[index] = { ...rows[index], [field === 'due_date' ? 'due_date' : 'name']: false }
      return { ...prev, work_items: rows }
    })
    setForm((prev) => ({
      ...prev,
      work_items: prev.work_items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }))
  }

  const addWorkItem = () => {
    setForm((prev) => ({ ...prev, work_items: [...prev.work_items, { ...EMPTY_WORK_ITEM }] }))
  }

  const removeWorkItem = (index) => {
    setForm((prev) => ({
      ...prev,
      work_items: prev.work_items.length <= 1
        ? [{ ...EMPTY_WORK_ITEM }]
        : prev.work_items.filter((_, itemIndex) => itemIndex !== index),
    }))
  }

  const buildPayload = () => ({
    object_id: form.object_id,
    type: form.type.trim(),
    name: (form.name || form.type).trim(),
    status: form.status,
    planned_date: form.planned_date || null,
    fact_date: form.fact_date || null,
    completion_date: form.completion_date || null,
    work_start_date: form.work_start_date || null,
    work_items: form.work_items
      .map((item, index) => ({
        id: item.id,
        name: (item.name || '').trim(),
        due_date: item.due_date || null,
        sort_order: index,
      }))
      .filter((item) => item.name || item.due_date),
  })

  // Откуда пришли: при заходе со страницы объекта возвращаемся туда же после сохранения.
  const cameFromObject = location.state?.from === 'object' && Boolean(location.state?.objectId)
  const objectBackPath = cameFromObject ? `/object/${encodeURIComponent(location.state.objectId)}` : null
  const backPath = objectBackPath
    || (isEdit ? `/company/budgets/${encodeURIComponent(editStageId)}` : '/company/budgets')

  // Валидация перед сохранением: дата старта обязательна; у заполненных разделов
  // название и ручной срок непустые. Намеренно НЕ сравниваем дату старта
  // с «Сроком завершения работ» — старт позже срока допустим и не блокирует сохранение.
  const validateBeforeSave = () => {
    const rows = form.work_items.map(() => ({ name: false, due_date: false }))
    let ok = true
    const workStartMissing = !form.work_start_date
    if (workStartMissing) ok = false
    form.work_items.forEach((item, index) => {
      const nameFilled = String(item.name || '').trim() !== ''
      const dueDateFilled = String(item.due_date ?? '').trim() !== ''
      if (!nameFilled && !dueDateFilled) return // пустую строку игнорируем
      if (!nameFilled) { rows[index].name = true; ok = false }
      if (!dueDateFilled) { rows[index].due_date = true; ok = false }
    })
    setFieldErrors({ work_start_date: workStartMissing, work_items: rows })
    return ok
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const payload = buildPayload()
    if (!payload.object_id) {
      setError('Выберите объект')
      return
    }
    if (!payload.type) {
      setError('Выберите тип стадии')
      return
    }
    if (!validateBeforeSave()) {
      setError('Заполните выделенные поля: дата старта работ обязательна, у разделов — название и срок.')
      return
    }
    try {
      setSaving(true)
      setError(null)
      const response = isEdit
        ? await api.put(`/company/object-stages/${encodeURIComponent(editStageId)}`, payload)
        : await api.post('/company/object-stages', payload)
      const nextStageId = response.data?.stage?.id || editStageId
      navigate(objectBackPath || `/company/budgets/${encodeURIComponent(nextStageId)}`)
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Не удалось сохранить стадию')
    } finally {
      setSaving(false)
    }
  }

  const openContractModal = () => {
    if (filteredContracts.length === 0) {
      setError('Нет договоров по выбранному объекту для создания бюджета стадии')
      return
    }
    const initial = filteredContracts[0]?.id || ''
    setContractSearch('')
    setSelectedContractId(initial)
    setContractModalOpen(true)
  }

  const handleAddBudgetConfirm = () => {
    if (!selectedContractId) return
    setContractModalOpen(false)
    navigate(`/company/contracts/${selectedContractId}/stages/new`, {
      state: {
        stageDraft: {
          object_stage_id: editStageId,
          distribution_type: form.type || undefined,
        },
      },
    })
  }

  const submitButtonLabel = saving
    ? 'Сохранение...'
    : isEdit ? 'Сохранить' : 'Создать стадию'

  if (loading) return <div className="loading">Загрузка...</div>

  return (
    <div className="company-page object-stage-form-page">
      <div className="page-header object-stage-form-header">
        <Button variant="ghost" onClick={() => navigate(backPath)} className="btn-back">
          ← Назад
        </Button>
        <div>
          <h1>{isEdit ? 'Редактирование стадии' : 'Создание стадии'}</h1>
          {isEdit ? <p>Паспорт стадии, бюджеты и работы</p> : null}
        </div>
      </div>

      {error && <div className="message message-error" role="alert">{error}</div>}

      <form className="object-stage-form" onSubmit={handleSubmit}>
        <section className="card object-stage-form-section">
          <h2 className="section-title">Паспорт стадии</h2>
          <div className="object-stage-form-grid">
            <FormGroup label="Объект" htmlFor="object-stage-object" required>
              <Select
                id="object-stage-object"
                aria-label="Объект"
                value={form.object_id}
                disabled={isEdit}
                onChange={(event) => handleField('object_id', event.target.value)}
              >
                <option value="">Выберите объект</option>
                {objects.map((object) => (
                  <option key={object.id} value={object.id}>{object.name || object.id}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Тип стадии" htmlFor="object-stage-type" required>
              <Select
                id="object-stage-type"
                aria-label="Тип стадии"
                value={form.type}
                onChange={(event) => handleField('type', event.target.value)}
              >
                <option value="">Выберите тип</option>
                {stageTypes.map((stageType) => (
                  <option key={stageType.id || stageType.name} value={stageType.name}>
                    {stageType.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Название стадии" htmlFor="object-stage-name">
              <Input
                id="object-stage-name"
                aria-label="Название стадии"
                value={form.name}
                onChange={(event) => handleField('name', event.target.value)}
              />
            </FormGroup>
            <FormGroup label="Статус" htmlFor="object-stage-status">
              <Select
                id="object-stage-status"
                value={form.status}
                onChange={(event) => handleField('status', event.target.value)}
              >
                <option value="не в работе">не в работе</option>
                <option value="в работе">в работе</option>
                <option value="работы завершены">работы завершены</option>
                <option value="приостановлен">приостановлен</option>
              </Select>
            </FormGroup>
            <FormGroup label="Срок завершения работ" htmlFor="object-stage-planned">
              <Input id="object-stage-planned" type="date" value={form.planned_date || ''} onChange={(event) => handleField('planned_date', event.target.value)} />
            </FormGroup>
            <FormGroup
              label="Дата старта работ"
              htmlFor="object-stage-work-start"
              required
              error={fieldErrors.work_start_date ? 'Укажите дату старта работ' : undefined}
            >
              <Input
                id="object-stage-work-start"
                aria-label="Дата старта работ"
                type="date"
                className={fieldErrors.work_start_date ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.work_start_date || undefined}
                value={form.work_start_date || ''}
                onChange={(event) => handleField('work_start_date', event.target.value)}
              />
            </FormGroup>
          </div>
        </section>

        {isEdit ? (
          <section className="card object-stage-form-section">
            <div className="object-stage-form-section-head">
              <h2 className="section-title">Бюджеты стадии</h2>
              <Button type="button" variant="secondary" size="sm" onClick={openContractModal}>
                <Icon name="plus" size={12} /> Добавить бюджет стадии
              </Button>
            </div>
            {stageBudgets.length > 0 ? (
              <div className="object-stage-budgets-table-wrap">
                <table className="object-stage-budgets-table" aria-label="Бюджеты стадии">
                  <thead>
                    <tr>
                      <th>Бюджет</th>
                      <th>Договор</th>
                      <th>Срок</th>
                      <th className="align-right">Сумма с НДС</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageBudgets.map((budget) => (
                      <tr key={budget.id}>
                        <td>{budget.name || budget.distribution_type || '—'}</td>
                        <td>{budget.contract_number || '—'}</td>
                        <td>{formatBudgetDate(budget.contract_date || budget.contract_date_legacy)}</td>
                        <td className="align-right amount-mono">{formatRub(budget.amount_with_vat)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Итого по бюджетам</td>
                      <td className="align-right amount-mono">{formatRub(budgetsTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="info-note">Бюджеты стадии пока не созданы. Добавьте бюджет через договор.</p>
            )}
          </section>
        ) : null}

        <section className="card object-stage-form-section">
          <div className="object-stage-form-section-head">
            <h2 className="section-title">Разделы</h2>
            <Button type="button" variant="secondary" size="sm" onClick={addWorkItem}>
              <Icon name="plus" size={12} /> Добавить раздел
            </Button>
          </div>
          <div className="object-stage-work-editor">
            {form.work_items.map((item, index) => {
              const rowErr = fieldErrors.work_items?.[index] || {}
              return (
              <div className="object-stage-work-row" key={item.id || index}>
                <FormGroup
                  label={`Название раздела ${index + 1}`}
                  htmlFor={`work-name-${index}`}
                  error={rowErr.name ? 'Укажите название' : undefined}
                >
                  <Input
                    id={`work-name-${index}`}
                    aria-label={`Название раздела ${index + 1}`}
                    className={rowErr.name ? 'is-invalid' : ''}
                    aria-invalid={rowErr.name || undefined}
                    value={item.name}
                    onChange={(event) => handleWorkItem(index, 'name', event.target.value)}
                  />
                </FormGroup>
                <FormGroup
                  label={`Срок работы ${index + 1}`}
                  htmlFor={`work-due-date-${index}`}
                  error={rowErr.due_date ? 'Укажите срок' : undefined}
                >
                  <Input
                    id={`work-due-date-${index}`}
                    aria-label={`Срок работы ${index + 1}`}
                    type="date"
                    className={rowErr.due_date ? 'is-invalid' : ''}
                    aria-invalid={rowErr.due_date || undefined}
                    value={item.due_date}
                    onChange={(event) => handleWorkItem(index, 'due_date', event.target.value)}
                  />
                </FormGroup>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="object-stage-work-remove"
                  onClick={() => removeWorkItem(index)}
                  aria-label={`Удалить раздел ${index + 1}`}
                >
                  <Icon name="trash" size={14} />
                </Button>
              </div>
              )
            })}
          </div>
        </section>

        <div className="form-actions object-stage-form-actions">
          <Button type="button" variant="secondary" onClick={() => navigate(backPath)}>
            Отмена
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {submitButtonLabel}
          </Button>
        </div>
      </form>

      {contractModalOpen ? (
        <ConfirmModal
          title="Добавить бюджет стадии"
          confirmLabel="Продолжить"
          confirmVariant="primary"
          onCancel={() => setContractModalOpen(false)}
          onConfirm={handleAddBudgetConfirm}
          confirmDisabled={!selectedContractId}
          message={(
            <div className="create-act-modal-content">
              <p>Выберите договор для создания бюджета стадии:</p>
              <div className="filter-group">
                <Input
                  type="text"
                  placeholder="Поиск по номеру договора"
                  value={contractSearch}
                  onChange={(event) => {
                    const next = event.target.value
                    setContractSearch(next)
                    const nextFiltered = filteredContracts.filter((contract) => (
                      String(contract.number || contract.id || '')
                        .toLowerCase()
                        .includes(next.trim().toLowerCase())
                    ))
                    if (nextFiltered.length === 0) {
                      setSelectedContractId('')
                    } else if (!nextFiltered.some((contract) => contract.id === selectedContractId)) {
                      setSelectedContractId(nextFiltered[0].id)
                    }
                  }}
                  aria-label="Поиск договора по номеру"
                />
              </div>
              <div className="filter-group">
                <Select
                  value={selectedContractId}
                  onChange={(event) => setSelectedContractId(event.target.value)}
                  aria-label="Договор"
                >
                  <option value="">— Выберите договор —</option>
                  {filteredContractsForCreate.map((contract) => (
                    <option key={contract.id} value={contract.id}>
                      {contractLabel(contract)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        />
      ) : null}
    </div>
  )
}

export default ObjectStageForm
